import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, ListOrdered, AlertTriangle } from 'lucide-react';
import config from '../config/config';
const GroupedAccountsSummary = () => {
  const [accountsData, setAccountsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const calculateValue = (debit, credit, type) => {
    if (['asset', 'expense'].includes(type.toLowerCase())) {
      return debit - credit;
    }
    return credit - debit;
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('No authentication token found.');
      setLoading(false);
      return;
    }

    axios.get(config.API_URL+'/api/pullJournalEntries', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then(response => {
      const grouped = {};

      response.data.data.forEach(entry => {
        const accountId = entry.account_id;
        if (!grouped[accountId]) {
          grouped[accountId] = {
            accountName: entry.name,
            accountType: entry.account_type,
            debit: 0,
            credit: 0,
          };
        }
        grouped[accountId].debit += parseFloat(entry.debit || 0);
        grouped[accountId].credit += parseFloat(entry.credit || 0);
      });

      const finalData = Object.values(grouped).map(acc => ({
        ...acc,
        value: calculateValue(acc.debit, acc.credit, acc.accountType),
      }));

      setAccountsData(finalData);
      setLoading(false);
    })
    .catch(() => {
      setError('Failed to load accounts summary.');
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin w-6 h-6 text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 flex justify-center items-center gap-2 mt-10">
        <AlertTriangle className="w-5 h-5" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 bg-white rounded-2xl shadow-lg mt-10">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <ListOrdered className="w-6 h-6 text-blue-500" />
          Account Summary
        </h2>
        <span className="text-sm text-gray-500">
          Grouped by Account ({accountsData.length} items)
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-100 text-left text-gray-600 uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3">Account Name</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3 text-right">Debit (KES)</th>
              <th className="px-4 py-3 text-right">Credit (KES)</th>
              <th className="px-4 py-3 text-right">Value (KES)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {accountsData.map((acc, index) => (
              <tr key={index} className="hover:bg-gray-50 transition">
                <td className="px-4 py-2 font-medium text-gray-800">{acc.accountName}</td>
                <td className="px-4 py-2 text-gray-500 capitalize">{acc.accountType}</td>
                <td className="px-4 py-2 text-right text-green-600 font-semibold">{acc.debit.toLocaleString()}</td>
                <td className="px-4 py-2 text-right text-red-600 font-semibold">{acc.credit.toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-bold text-gray-800">{acc.value.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GroupedAccountsSummary;
